import { LiftType } from "../flatbuffers/generated/archive/lift-type";
import { uuidToString } from "./uuid";

/**
 * Numeric device-type constants matching the server's Messages API `device_type` field.
 * These occupy bits 21-20 of the composite debounce key (2 bits, values 0-3).
 */

export const DEVICE_ELEVATOR = 0;
export const DEVICE_ESCALATOR = 1;
export const DEVICE_STATUS = 2;

/**
 * Pack device type, MessageContent id, and enum code into a single 32-bit integer.
 *
 * Layout (22 bits used):
 *   bits 21-20 : device type   (2 bits, 0-3)
 *   bits 19-16 : message content id (4 bits, 0-15)
 *   bits 15-0  : enum code     (16 bits, 0-65535)
 */
export function debounceKey(deviceType: number, messageContent: number, code: number): number {
  return (deviceType % 4) * 1048576 + (messageContent % 16) * 65536 + (code % 65536);
}

/**
 * Map the server's `device_type` string to a numeric constant for composite keys.
 */
export function deviceTypeFromString(deviceType: string): number {
  switch (deviceType) {
    case LiftType[LiftType.ELEVATOR]:
      return DEVICE_ELEVATOR;
    case LiftType[LiftType.ESCALATOR]:
      return DEVICE_ESCALATOR;
    case "STATUS":
      return DEVICE_STATUS;
    default:
      return DEVICE_ELEVATOR;
  }
}

/**
 * Map the FlatBuffer-generated `LiftType` numeric enum to a composite-key device type.
 * Use this when the source is a FlatBuffer T-class (e.g., DebounceOverrideT.fbsDeviceType,
 * KeyLightT.fbsDeviceType) — those fields are numeric enum values, not strings.
 */
export function deviceTypeFromLiftType(deviceType: LiftType): number {
  switch (deviceType) {
    case LiftType.ELEVATOR:
      return DEVICE_ELEVATOR;
    case LiftType.ESCALATOR:
      return DEVICE_ESCALATOR;
    default:
      return DEVICE_ELEVATOR;
  }
}

/**
 * Resolves the debounce time for a given bank/lift/message using 3-tier lookup:
 *   1. Lift-specific override  (bankId:liftId:compositeKey)
 *   2. Bank-wide override      (bankId:*:compositeKey)
 *   3. Adapter key default     (compositeKey as number)
 *
 * The composite key encodes device type, message content, and enum code
 * into a single number, preventing collisions between different message types
 * that share the same enum_id.
 *
 * Returns 0 when nothing is configured (display immediately).
 */
export default function resolveDebounceTime(
  overrideMap: Map<string, number>,
  defaultMap: Map<number, number>,
  bankId: string,
  liftId: string,
  compositeKey: number,
): number {
  return (
    overrideMap.get(`${bankId}:${liftId}:${compositeKey}`) ??
    overrideMap.get(`${bankId}:*:${compositeKey}`) ??
    defaultMap.get(compositeKey) ??
    0
  );
}

/**
 * One entry of the adapter-default map: the composite key and the debounce time of a
 * decoded `GET /keys/light/` item. `resolveDebounceTime` reads the map this fills as its
 * `defaultMap`. Returns `undefined` when the key has no debounce time, no message content
 * or no message value, so it contributes no entry.
 */
export function keyDebounceEntry(key: any): [number, number] | undefined {
  const dTime = key.debounceTime ?? key.debounce_time;
  const mc = key.messageContent ?? key.message_content;
  const mv = key.messageValue ?? key.message_value;
  const devType: LiftType = key.fbsDeviceType ?? key.fbs_device_type ?? LiftType.ELEVATOR;
  if (dTime && mc && mv !== undefined) {
    const dt = deviceTypeFromLiftType(devType);
    const composite = debounceKey(dt, mc, mv);
    return [composite, dTime];
  }
  return undefined;
}

/**
 * One entry of the override map: the map key and the debounce time of a decoded
 * `GET /debounce-overrides/` item. `resolveDebounceTime` reads the map this fills as its
 * `overrideMap`. The key is `bankId:liftId:composite` for a lift override and
 * `bankId:*:composite` for a bank-wide one. Returns `undefined` when the override has no
 * message content or no message value.
 */
export function overrideDebounceEntry(override: any): [string, number] | undefined {
  const mc = override.messageContent ?? override.message_content;
  const mv = override.messageValue ?? override.message_value;
  const devType: LiftType = override.fbsDeviceType ?? override.fbs_device_type ?? LiftType.ELEVATOR;
  if (!mc || mv === undefined) return undefined;

  const dt = deviceTypeFromLiftType(devType);
  const composite = debounceKey(dt, mc, mv);
  // FlatBuffer T-classes expose UUIDs as UuidT objects (or strings on legacy paths);
  // stringify before using as a Map key so it matches the WS-side lookup key.
  const bankIdStr =
    typeof override.bankId === "string" ? override.bankId : uuidToString(override.bankId);
  if (override.liftId) {
    const liftIdStr =
      typeof override.liftId === "string" ? override.liftId : uuidToString(override.liftId);
    return [`${bankIdStr}:${liftIdStr}:${composite}`, override.debounceTime];
  }
  return [`${bankIdStr}:*:${composite}`, override.debounceTime];
}
